/**
 * Fast-video (condensed run video) types and time math.
 *
 * A run's "fast" video is the full video with dead (no-motion) spans cut out.
 * `cutIntervals` records exactly which spans of the FULL video were removed,
 * which makes the mapping between the two timebases pure arithmetic:
 *
 *   fastMs = fullMs - (total cut time before fullMs)
 *
 * The UI uses these maps to keep panels that operate in the full timebase
 * (activity panel, step sync, diagnostics) in sync while the fast variant
 * plays, and to preserve playback position when toggling variants.
 *
 * @module fast-video
 */

import type { VideoTimeline } from './browser-stream.js';

/** A span of the FULL video that was removed from the fast variant. */
export type CutInterval = {
  /** Cut start, ms in the full video's timebase (inclusive) */
  fullStartMs: number;
  /** Cut end, ms in the full video's timebase (exclusive) */
  fullEndMs: number;
};

/**
 * Timeline persisted for the fast variant: remapped entries plus the cut map
 * needed to translate positions between the full and fast timebases.
 */
export type FastVideoTimeline = VideoTimeline & {
  cutIntervals: CutInterval[];
  fullDurationMs: number;
  fastDurationMs: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Total milliseconds removed from the full video strictly before `fullMs`.
 * A position inside a cut counts only the portion of that cut before it,
 * so mapping it lands on the cut's kept boundary.
 */
export function removedBefore(fullMs: number, cuts: CutInterval[]): number {
  let removed = 0;
  for (const cut of cuts) {
    if (fullMs <= cut.fullStartMs) break;
    removed += Math.min(fullMs, cut.fullEndMs) - cut.fullStartMs;
  }
  return removed;
}

/**
 * Map a position in the full video to the fast video. Positions inside a cut
 * clamp to the cut's kept boundary (the moment just before the skip).
 */
export function mapFullToFast(
  fullMs: number,
  cuts: CutInterval[],
  fastDurationMs: number
): number {
  return clamp(fullMs - removedBefore(fullMs, cuts), 0, Math.max(0, fastDurationMs));
}

/**
 * Map a position in the fast video back to the full video. A fast position
 * exactly at a cut boundary maps to the cut's start (not its end) so
 * full→fast→full round-trips are stable for kept positions.
 */
export function mapFastToFull(
  fastMs: number,
  cuts: CutInterval[],
  fullDurationMs: number
): number {
  const safeFastMs = Math.max(0, fastMs);
  let removed = 0;
  for (const cut of cuts) {
    const fastBoundary = cut.fullStartMs - removed;
    if (safeFastMs <= fastBoundary) break;
    removed += cut.fullEndMs - cut.fullStartMs;
  }
  return clamp(safeFastMs + removed, 0, Math.max(0, fullDurationMs));
}
